import { useEffect } from "react";
import { MessageInstance } from "antd5/es/message/interface";
import isEqual from "lodash.isequal";
import qs from "qs";

import {
  convertFrameworkRelationshipFilterToFrameworkFilterOptions,
  DEFAULT_FILTERS,
  DEFAULT_NOTICE_COLUMNS,
  DEFAULT_NOTICE_SORT_ORDER,
  EMPTY_FILTERS,
  NoticeFilters,
} from "components/notices/utils";
import {
  paramsToDateRangeObj,
  paramsToNullableDateRangeObj,
  paramsToNumericRangeObj,
} from "components/record_search/types";
import { INLINE_FILTERS, useVariableValue } from "lib/featureFlags";
import {
  ColumnSettingFieldEnum,
  PinnedSavedViewDto,
  RecordSearchRequestDtoProcurementStageQualificationsEnum,
  SavedViewType,
} from "lib/generated/app-api";
import { usePinView } from "lib/hooks/api/notices/views/usePinView";
import { SignalSettings, useSignalSettingsGQL } from "lib/hooks/api/teams/useSignalSettingsGQL";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { parseBooleanValue, parseNumberValue, useURLState } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import { urlParamToArray, urlParamToTypedArray } from "lib/search/url_params";
import { EventNames, useTracking } from "lib/tracking";
import { NoticesStagesv2, SupplierMentionType } from "lib/types/graphQLEnums";
import {
  ALL_COMPETITORS_TOKEN,
  ALL_CPV_CODES_TOKEN,
  ALL_KEYWORDS_TOKEN,
  ALL_PARTNERS_TOKEN,
  Status,
} from "lib/types/models";
import { simpleArrayDedupe } from "lib/utils";
import { useAllViews } from "../../lib/hooks/api/notices/views/useAllViews";
import { usePinnedViews } from "../../lib/hooks/api/notices/views/usePinnedViews";
import { useStotlesApi } from "../../lib/stotlesApiContext";
import { createSavedViewSelectedEvent } from "./tracking";

export function convertViewToNoticeFilters({
  savedView,
  isInlineFiltersEnabled,
  signalSettings,
}: {
  savedView: SavedViewType;
  isInlineFiltersEnabled?: boolean;
  signalSettings?: SignalSettings;
}): NoticeFilters {
  const { view, isStandardView } = savedView;

  let relevanceScore = view.filterSettings.relevanceScore || [];
  const supplierGuids = view.filterSettings.suppliers?.guid || [];
  const cpvCodes = view.filterSettings.cpvCodes || [];
  let keywords = view.filterSettings.keywords || [];

  if (isStandardView && isInlineFiltersEnabled) {
    keywords = [...(signalSettings?.keywords || []), "SIGNALS:ALL_KEYWORDS"].flat();
  }

  if (signalSettings) {
    convertSignalTokensToFilters(supplierGuids, cpvCodes, keywords, signalSettings);
  }

  let procurementStageQualifications = view.filterSettings.procurementStageQualifications;

  if (isStandardView && !procurementStageQualifications) {
    procurementStageQualifications = DEFAULT_FILTERS.procurementStageQualifications;
  }

  const noticeLists =
    view.filterSettings.noticeLists?.map((noticeList) => noticeList.toString()) || [];

  //TODO: remove this after migrating saved views to use noticeLists instead of noticeList
  if (!view.filterSettings.noticeLists && view.filterSettings.noticeList) {
    noticeLists?.push(view.filterSettings.noticeList.toString());
  }

  // check if procurementStageQualifications has not_relevant or pre_engage_not_relevant then isHideNoticesEnabled is true
  const includeHiddenNotices =
    procurementStageQualifications?.includes(
      RecordSearchRequestDtoProcurementStageQualificationsEnum.NotRelevant,
    ) ||
    procurementStageQualifications?.includes(
      RecordSearchRequestDtoProcurementStageQualificationsEnum.PreEngageNotRelevant,
    );

  return {
    procurementStageQualifications: procurementStageQualifications || [],
    signals: [],
    assignee: view.filterSettings.assignee || [],
    buyerListIds: view.filterSettings.buyerListIds || [],
    buyers: view.filterSettings.buyerGuids || [],
    buyerCategories: view.filterSettings.buyerCategoryId || [],
    buyerCountryRegions: {
      countries: view.filterSettings.buyerCountryCodes || [],
      regions: view.filterSettings.buyerRegionIds || [],
      selected: [],
    },
    value: {
      to: view.filterSettings.value?.to || undefined,
      from: view.filterSettings.value?.from || undefined,
      hideNulls:
        view.filterSettings.valueNulls === undefined ? false : !view.filterSettings.valueNulls,
    },
    text: view.filterSettings.text || "",
    stage:
      // TODO: Remove once we migrate saved views to the new stages (procurement-act)
      convertStagesForProcurementAct(view.filterSettings.stage as NoticeFilters["stage"]) || [],
    relevanceScore,
    awardDate: {
      filter: {
        ...view.filterSettings.awardDate,
        to: view.filterSettings.awardDate?.to?.toISOString(),
        from: view.filterSettings.awardDate?.from?.toISOString(),
      },
      hideNulls:
        view.filterSettings.awardDate === undefined ? false : !view.filterSettings.awardDate,
    },
    closeDate: {
      filter: {
        ...view.filterSettings.closeDate,
        to: view.filterSettings.closeDate?.to?.toISOString(),
        from: view.filterSettings.closeDate?.from?.toISOString(),
      },
      hideNulls:
        view.filterSettings.closeNulls === undefined ? false : !view.filterSettings.closeNulls,
    },
    expiryDate: {
      filter: {
        ...view.filterSettings.expiryDate,
        to: view.filterSettings.expiryDate?.to?.toISOString(),
        from: view.filterSettings.expiryDate?.from?.toISOString(),
      },
      hideNulls:
        view.filterSettings.expiryNulls === undefined ? false : !view.filterSettings.expiryNulls,
    },
    publishDate: {
      ...view.filterSettings.publishDate,
      to: view.filterSettings.publishDate?.to?.toISOString(),
      from: view.filterSettings.publishDate?.from?.toISOString(),
    },
    sort: {
      field: view.sortOrder?.field || DEFAULT_NOTICE_SORT_ORDER.field,
      order:
        (view.sortOrder?.order as NoticeFilters["sort"]["order"]) ||
        DEFAULT_NOTICE_SORT_ORDER.order,
    },
    suppliers: view.filterSettings.suppliers?.id || [],
    supplierGuids: simpleArrayDedupe(supplierGuids),
    supplierSme: view.filterSettings.suppliers?.isSme ?? "",
    noticeLists: noticeLists || [],
    frameworkIds: view.filterSettings.frameworks?.ids || [],
    frameworkFilterOptions:
      convertFrameworkRelationshipFilterToFrameworkFilterOptions(
        view.filterSettings.frameworks?.frameworkRelationship,
      ) || "",
    supplierMentionType:
      (view.filterSettings.suppliers?.mentionType as SupplierMentionType) || undefined,
    keywords: simpleArrayDedupe(keywords),
    excludeKeywords: view.filterSettings.excludeKeywords || [],
    cpvCodes: simpleArrayDedupe(cpvCodes),
    ...(includeHiddenNotices ? { includeHiddenNotices } : {}),
  };
}

function convertSignalTokensToFilters(
  supplierGuids: string[],
  cpvCodes: string[],
  keywords: string[],
  signalSettings: SignalSettings,
) {
  const {
    competitors,
    partners,
    cpvCodes: cpvCodeSignals,
    keywords: keywordSignals,
  } = signalSettings;
  // Add all competitors and partners to the guids if the token is present
  if (supplierGuids.includes(ALL_COMPETITORS_TOKEN) && competitors && competitors.length > 0) {
    supplierGuids.push(...competitors.map((c) => c.id));
  }
  if (supplierGuids.includes(ALL_PARTNERS_TOKEN) && partners && partners.length > 0) {
    supplierGuids.push(...partners.map((p) => p.id));
  }

  if (cpvCodes.includes(ALL_CPV_CODES_TOKEN) && cpvCodeSignals && cpvCodeSignals.length > 0) {
    cpvCodes.push(...cpvCodeSignals);
  }
  if (keywords.includes(ALL_KEYWORDS_TOKEN) && keywordSignals && keywordSignals.length > 0) {
    keywords.push(...keywordSignals);
  }
}

type FiltersArrayKeys = keyof Pick<
  NoticeFilters,
  "signals" | "procurementStageQualifications" | "assignee"
>;

type FiltersObjectKeys = keyof Pick<NoticeFilters, "publishDate">;

function convertStagesForProcurementAct(currentStages?: (Status | NoticesStagesv2)[]) {
  // TODO: Remove this logic once we have migrated all views to the new stage values (procurement-act)
  const oldStages = ["OPEN", "CLOSED", "AWARDED", "PRE_TENDER", "STALE_PRE_TENDER"];

  if (!currentStages?.some((s) => oldStages.includes(s))) {
    return currentStages;
  }
  const newStages = currentStages.filter((s) => !oldStages.includes(s)) as NoticesStagesv2[];

  // https://www.notion.so/stotles/Stage-definition-work-184b27cc426d805fb1c1c581114f6b00?pvs=4#18fb27cc426d802cbf9ed4d33313a3d3
  if (currentStages.includes("PRE_TENDER")) {
    newStages.push(NoticesStagesv2.Pipeline, NoticesStagesv2.PreTenderV2);
  }
  if (currentStages.includes("STALE_PRE_TENDER")) {
    newStages.push(NoticesStagesv2.StalePreTenderV2);
  }
  if (currentStages.includes("OPEN")) {
    newStages.push(NoticesStagesv2.OpenTender);
  }
  if (currentStages.includes("CLOSED")) {
    newStages.push(NoticesStagesv2.ClosedTender, NoticesStagesv2.CancelledTender);
  }
  if (currentStages.includes("AWARDED")) {
    newStages.push(
      NoticesStagesv2.AwardedContract,
      NoticesStagesv2.ExpiredContract,
      NoticesStagesv2.CancelledAward,
    );
  }
  return newStages;
}

export function parseUrlState(val?: NoticeFilters, defaultFilters?: NoticeFilters) {
  if (!val) {
    return defaultFilters || DEFAULT_FILTERS;
  }

  // if array values are undefined set as an empty array otherwise default will override
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    const key = k as keyof NoticeFilters;
    if (
      typeof DEFAULT_FILTERS[key] === "object" &&
      !Array.isArray(DEFAULT_FILTERS[key]) &&
      !val[key]
    ) {
      val[key as FiltersObjectKeys] = {};
    }
    if (Array.isArray(DEFAULT_FILTERS[key]) && !val[key]) {
      val[key as FiltersArrayKeys] = [];
    }
  });

  const currentStages = convertStagesForProcurementAct(val.stage);

  return {
    ...defaultFilters,
    ...val,
    stage: currentStages || [],
    value: {
      from: parseNumberValue(val.value?.from),
      to: parseNumberValue(val.value?.to),
      hideNulls: parseBooleanValue(val.value?.hideNulls),
    },
    awardDate: {
      filter: val.awardDate?.filter,
      hideNulls: parseBooleanValue(val.awardDate?.hideNulls),
    },
    closeDate: {
      filter: val.closeDate?.filter,
      hideNulls: parseBooleanValue(val.closeDate?.hideNulls),
    },
    expiryDate: {
      filter: val.expiryDate?.filter,
      hideNulls: parseBooleanValue(val.expiryDate?.hideNulls),
    },
    suppliers: val.suppliers.map((id) => Number(id)) || [],
    supplierGuids: val.supplierGuids || [],
    supplierSme: parseBooleanValue(val.supplierSme) ?? "",
    includeHiddenNotices: parseBooleanValue(val.includeHiddenNotices),
    sort: {
      field: val.sort?.field || defaultFilters?.sort?.field || DEFAULT_NOTICE_SORT_ORDER.field,
      order: val.sort?.order || defaultFilters?.sort?.field || DEFAULT_NOTICE_SORT_ORDER.order,
    },
  };
}

function parseOldUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const currentView = urlParams.get("current_view_id");
  const currentPage = urlParams.get("page");
  const pageSize = urlParams.get("page_size");

  const filters: Partial<NoticeFilters> = {};

  filters.text = urlParams.get("text") || undefined;
  filters.buyers = urlParamToArray(urlParams, "buyer_guids");
  filters.stage = urlParamToArray(urlParams, "stage")?.map((s) => s.toUpperCase()) as Status[];
  const closeDate = paramsToNullableDateRangeObj(urlParams, "close_date");
  if (closeDate) {
    filters.closeDate = {
      filter: {
        ...closeDate.filter,
        from: closeDate.filter?.from?.toISOString(),
        to: closeDate.filter?.to?.toISOString(),
      },
      hideNulls: !closeDate.includeNulls,
    };
  }
  const expiryDate = paramsToNullableDateRangeObj(urlParams, "expiry_date");
  if (expiryDate) {
    filters.expiryDate = {
      filter: {
        ...expiryDate.filter,
        from: expiryDate.filter?.from?.toISOString(),
        to: expiryDate.filter?.to?.toISOString(),
      },
      hideNulls: !expiryDate.includeNulls,
    };
  }
  const publishDate = paramsToDateRangeObj(urlParams, "publish_date");
  if (publishDate) {
    filters.publishDate = {
      ...publishDate,
      from: publishDate.from?.toISOString(),
      to: publishDate.to?.toISOString(),
    };
  }
  const signalIds = urlParamToArray(urlParams, "any_signals_ids");
  const signalCategories = urlParamToArray(urlParams, "any_signals_categories");
  if (signalIds || signalCategories) {
    filters.signals = [...(signalIds || []), ...(signalCategories || [])];
  }
  filters.buyerCategories = urlParamToArray(urlParams, "buyer_category_id");
  const value = paramsToNumericRangeObj(urlParams, "value");
  if (value) {
    filters.value = {
      from: value.from,
      to: value.to,
      hideNulls: !value.includeNulls,
    };
  }
  filters.procurementStageQualifications = urlParamToTypedArray(
    urlParams,
    "procurement_stage_qualifications",
    (p) => p as RecordSearchRequestDtoProcurementStageQualificationsEnum,
  );
  filters.assignee = urlParamToArray(urlParams, "assignee");
  filters.buyerListIds = urlParamToArray(urlParams, "buyer_list_ids");

  const sortField = urlParams.get("sort");
  const sortOrder = urlParams.get("sort_order");
  if (sortField || sortOrder) {
    filters.sort = {
      field: sortField || "publish_date",
      order: (sortOrder as NoticeFilters["sort"]["order"]) || "desc",
    };
  }

  const params = {
    noticeFilters: isEqual(filters, {}) ? undefined : filters,
    current_view_id: currentView,
    page: currentPage,
    page_size: pageSize,
  };

  // only check filters as the other params are handled by the useURLState hook
  if (isEqual(filters, {}) || Object.values(filters).every((v) => !v)) {
    return undefined;
  }

  // signal were hidden by default so if they are not set then set them to the default
  if (!filters.signals) {
    filters.signals = DEFAULT_FILTERS.signals;
  }

  return params;
}

type MyFeedViewsResponse = {
  pinnedViews: PinnedSavedViewDto[] | undefined;
  allViews: SavedViewType[] | undefined;
  selectedView: SavedViewType | undefined;
  isLoadingViews: boolean;
  recentViews: string[];
  setUrlViewId: (newValue: string) => void;
  setRecentViews: (newValue: string[]) => void;
  setShowNoView: (newValue: boolean | undefined) => void;
};

export function useSavedViews(isEnabled: boolean = true): MyFeedViewsResponse {
  const { data: pinnedViews, isLoading: isLoadingPinned } = usePinnedViews({
    enabled: isEnabled,
  });

  const { data: allViews, isLoading: isLoadingAllViews } = useAllViews({
    enabled: isEnabled,
  });

  const defaultView = allViews?.find((v) => v.id === pinnedViews?.[0].savedViewId) || allViews?.[0];

  const isLoadingViews = isLoadingPinned || isLoadingAllViews;

  const [urlViewId, setUrlViewId] = useURLState("current_view_id", "");
  const urlView = allViews?.find((v) => v.id === urlViewId);

  const [recentViews, setRecentViews] = useLocalStorage<string[]>("RECENT_VIEWS", []);
  const recentView = recentViews[0] ? allViews?.find((v) => v.id === recentViews[0]) : undefined;

  // used to determine when a user purposely deselects a view
  const [showNoView, setShowNoView] = useURLState<boolean | undefined>(
    "showNoView",
    false,
    parseBooleanValue,
  );

  const selectedView = showNoView ? undefined : urlView || recentView || defaultView;

  return {
    pinnedViews,
    allViews,
    selectedView,
    isLoadingViews,
    recentViews,
    setUrlViewId,
    setRecentViews,
    setShowNoView,
  };
}

export function useMyFeedPageState() {
  const { logEvent } = useTracking();

  const isInlineFiltersEnabled = useVariableValue(INLINE_FILTERS, false);

  useEffect(() => {
    const convertedParams = parseOldUrl();
    if (convertedParams) {
      const newQsValue = qs.stringify(convertedParams, { arrayFormat: "brackets" });
      window.location.assign(`${routes.notices}?${newQsValue}`);
    }
  }, []);

  const { data: signals, isLoading: isLoadingSignals } = useSignalSettingsGQL();

  const {
    pinnedViews,
    allViews,
    selectedView,
    isLoadingViews,
    recentViews,
    setUrlViewId,
    setRecentViews,
    setShowNoView,
  } = useSavedViews();

  const [filters, setFilters] = useURLState<NoticeFilters | undefined>(
    "noticeFilters",
    DEFAULT_FILTERS,
    (val) =>
      parseUrlState(
        val as NoticeFilters,
        selectedView &&
          convertViewToNoticeFilters({
            savedView: selectedView,
            isInlineFiltersEnabled,
            signalSettings: signals,
          }),
      ),
  );

  // Report pages generate search links with buyer id filters.
  // These are no longer supported by the search page, so we need to fetch
  // the buyers by id to convert them to buyer GUID filters.
  // Unfortunately converting reports to use buyer GUIDS is signficantly harder than
  // this small hack which will only affect the one case of links from reports,
  // as no other part of code uses buyer id filters.
  //
  // TODO: Remove this once we get rid of buyer id filters passed in from the report pages
  const api = useStotlesApi();
  useEffect(() => {
    if (!filters?.buyerIds || filters.buyerIds.length == 0) {
      return;
    }
    const buyerIds = filters.buyerIds;
    // Run this async - there is unfortunately a small risk of first fetching
    // the incorrect data and then doing the right search once the filters are updated.
    void (async () => {
      const searchBuyersResponse = await api.searchBuyers({
        id: buyerIds,
        limit: buyerIds.length,
      });

      setFilters(
        (oldFilters) =>
          oldFilters && {
            ...oldFilters,
            buyerIds: undefined,
            buyers: searchBuyersResponse.results.map((b) => b.guid),
          },
      );
    })();
  }, [api, filters?.buyerIds, setFilters]);

  const [urlTableColumns, setUrlTableColumns] = useURLState<ColumnSettingFieldEnum[] | undefined>(
    "noticeTableColumns",
    undefined,
  );

  let tableColumns;
  if (urlTableColumns) {
    tableColumns = urlTableColumns.map((field) => ({ field }));
  } else if (
    selectedView?.view.tableSettings.columns &&
    selectedView?.view.tableSettings.columns.length !== 0
  ) {
    tableColumns = selectedView.view.tableSettings.columns;
  } else {
    tableColumns = DEFAULT_NOTICE_COLUMNS;
  }

  const setSelectedView = (viewId?: string, viewName?: string, context?: string | undefined) => {
    if (!viewId) {
      setShowNoView(true);
      setUrlViewId("");
      setFilters(EMPTY_FILTERS);
      setUrlTableColumns(undefined);
      return;
    }

    setUrlViewId(viewId);
    if (viewId) {
      setRecentViews([viewId, ...recentViews.filter((v) => v !== viewId)]);
    }
    setFilters(undefined);
    setShowNoView(undefined);
    setUrlTableColumns(undefined);
    viewId &&
      logEvent(
        EventNames.viewSelected,
        createSavedViewSelectedEvent(viewName, viewId, context, isInlineFiltersEnabled),
      );
  };

  const clearViewAndFilters = () => {
    setShowNoView(true);
    setUrlViewId("");
    // clear all filters except text
    setFilters({ ...EMPTY_FILTERS, text: filters?.text || "" });
    setUrlTableColumns(undefined);
  };

  const viewAsNoticeFilter =
    selectedView &&
    convertViewToNoticeFilters({
      savedView: selectedView,

      isInlineFiltersEnabled,
      signalSettings: signals,
    });

  const filterValue = filters || viewAsNoticeFilter || DEFAULT_FILTERS;

  const strippedFilters = removeUndefinedProperties(removeEdgeCaseFilters(filterValue));
  const strippedViewAsNoticeFilter = removeUndefinedProperties(
    removeEdgeCaseFilters(viewAsNoticeFilter),
  );

  let hasUserModifiedFilters = false;
  if (isInlineFiltersEnabled) {
    hasUserModifiedFilters = !isEqual(strippedFilters, strippedViewAsNoticeFilter);
  } else {
    hasUserModifiedFilters = !isEqual(filterValue, viewAsNoticeFilter);
  }

  const hasUserModifiedTableSettings = !isEqual(
    tableColumns,
    selectedView?.view.tableSettings.columns,
  );
  const hasUserModifiedView = hasUserModifiedFilters || hasUserModifiedTableSettings;

  return {
    pinnedViews,
    allViews,
    selectedView,
    setSelectedView,
    filters: filterValue,
    setFilters,
    clearViewAndFilters,
    isLoadingViews,
    isLoadingSignals,
    hasUserModifiedView,
    hasUserModifiedFilters,
    hasUserModifiedTableSettings,
    tableColumns,
    setTableColumns: setUrlTableColumns,
  };
}

/**
 *
 * @param message
 * Checks that the view in the URL is available to the user and pins it if isnt pinned.
 */
export function useViewUrlCheck(message: MessageInstance) {
  const { data: allViews } = useAllViews();
  const { data: pinnedViews } = usePinnedViews();
  const [urlViewId, setUrlViewId] = useURLState("current_view_id", "");
  const { mutate: pinView } = usePinView();

  useEffect(() => {
    if (
      urlViewId &&
      pinnedViews &&
      allViews &&
      !pinnedViews.find((v) => v.savedViewId === urlViewId)
    ) {
      const view = allViews.find((v) => v.id === urlViewId);
      if (view && view.id) {
        return pinView({ id: view.id, name: view.name });
      }
      void message.error("The view in your URL is not available.");
      return setUrlViewId("");
    }
  }, [allViews, message, pinView, pinnedViews, setUrlViewId, urlViewId]);
}

/**
 *
 * @param obj
 * @returns An object with all undefined properties removed and procurementStageQualifications set to an empty object
 */
function removeUndefinedProperties(obj: unknown): unknown {
  // return non objects
  if (typeof obj !== "object" || obj === null || obj === undefined) {
    return obj;
  }

  const newObj: Record<string, unknown> = {};
  for (const key in obj as Record<string, unknown>) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // This is a deprecated filter that is stored on our default views despite being deprecated.
      // We need to set it to an empty object to avoid comparisons failing.
      if (key === "procurementStageQualifications") {
        newObj[key] = {};
        continue;
      }
      const value = (obj as Record<string, unknown>)[key];
      if (value !== undefined) {
        const processedValue = removeUndefinedProperties(value);
        if (processedValue !== undefined) {
          newObj[key] = processedValue;
        }
      }
    }
  }
  return newObj;
}

/**
 *
 * @param filters
 * @returns A copy of the filters with the edge case filters removed.
 * The edge case filters are empty states that differ between saved views and the stored url state.
 */
function removeEdgeCaseFilters(filters: NoticeFilters | undefined): NoticeFilters {
  if (!filters) {
    return DEFAULT_FILTERS;
  }

  let newFilters: NoticeFilters = JSON.parse(JSON.stringify(filters));

  // Award date
  if (
    newFilters.awardDate?.filter &&
    newFilters.awardDate.filter.to === undefined &&
    newFilters.awardDate.filter.from === undefined
  ) {
    delete newFilters.awardDate.filter;
  }
  if (newFilters.awardDate?.filter?.relativeFrom === undefined) {
    delete newFilters.awardDate?.filter?.relativeFrom;
  }
  if (newFilters.awardDate?.filter?.relativeTo === undefined) {
    delete newFilters.awardDate?.filter?.relativeTo;
  }

  // Close date
  if (
    newFilters.closeDate?.filter &&
    newFilters.closeDate.filter.to === undefined &&
    newFilters.closeDate.filter.from === undefined
  ) {
    delete newFilters.closeDate.filter;
  }
  if (newFilters.closeDate?.filter?.relativeFrom === undefined) {
    delete newFilters.closeDate?.filter?.relativeFrom;
  }
  if (newFilters.closeDate?.filter?.relativeTo === undefined) {
    delete newFilters.closeDate?.filter?.relativeTo;
  }

  // Expiry date
  if (
    newFilters.expiryDate?.filter &&
    newFilters.expiryDate.filter.to === undefined &&
    newFilters.expiryDate.filter.from === undefined
  ) {
    delete newFilters.expiryDate.filter;
  }
  if (newFilters.expiryDate?.filter?.relativeFrom === undefined) {
    delete newFilters.expiryDate?.filter?.relativeFrom;
  }
  if (newFilters.expiryDate?.filter?.relativeTo === undefined) {
    delete newFilters.expiryDate?.filter?.relativeTo;
  }

  // Publish date
  if (newFilters.publishDate?.to === undefined) {
    delete newFilters.publishDate.to;
  }
  if (newFilters.publishDate?.from === undefined) {
    delete newFilters.publishDate.from;
  }
  if (newFilters.publishDate?.relativeFrom === undefined) {
    delete newFilters.publishDate.relativeFrom;
  }
  if (newFilters.publishDate?.relativeTo === undefined) {
    delete newFilters.publishDate.relativeTo;
  }

  // Buyer country regions
  if (newFilters.buyerCountryRegions?.countries?.length === 0) {
    delete newFilters.buyerCountryRegions.countries;
  }
  if (newFilters.buyerCountryRegions?.regions?.length === 0) {
    delete newFilters.buyerCountryRegions.regions;
  }
  if (
    newFilters.buyerCountryRegions?.selected?.length === 0 &&
    newFilters.buyerCountryRegions?.selected?.length === 0
  ) {
    delete newFilters.buyerCountryRegions.selected;
  }

  // Value
  if (newFilters.value?.to === undefined) {
    delete newFilters.value.to;
  }
  if (newFilters.value?.from === undefined) {
    delete newFilters.value.from;
  }

  return newFilters;
}
